/**
 * Experience-level translation — client side.
 *
 * The selectable values come from artha-ai's /jobs/filters `experience_levels`
 * options (entry_level / junior / mid_level / senior / lead). The API filters
 * jobs by numeric years (exp_min / exp_max), so the selected level(s) are
 * translated here to the exact year ranges the filter counts are computed
 * with. This table mirrors EXPERIENCE_RANGES in artha-ai's
 * filter_query_builder.py — if the vocabulary changes there, update here.
 */

const YEARS: Record<string, { min: number; max: number | null }> = {
  entry: { min: 0, max: 1 },
  fresher: { min: 0, max: 1 },
  internship: { min: 0, max: 1 },
  junior: { min: 1, max: 3 },
  mid: { min: 3, max: 6 },
  senior: { min: 6, max: 10 },
  lead: { min: 10, max: null },
};

/** "Entry_Level" / "mid-level" / "Senior Level" -> "entry" / "mid" / "senior". */
function normalizeLevel(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[_\s-]?level$/, "")
    .trim();
}

function parseExperienceRange(value: string): { min: number; max: number | null } | undefined {
  const normalized = normalizeLevel(value);
  if (YEARS[normalized]) {
    return YEARS[normalized];
  }
  const clean = value.trim().toLowerCase().replace(/_years?$|\s*years?$/, "");
  const rangeMatch = clean.match(/^(\d+)[-_](\d+)$/);
  if (rangeMatch) {
    return { min: Number(rangeMatch[1]), max: Number(rangeMatch[2]) };
  }
  const plusMatch = clean.match(/^(\d+)\+$/);
  if (plusMatch) {
    return { min: Number(plusMatch[1]), max: null };
  }
  return undefined;
}

export interface ExperienceYears {
  exp_min?: number;
  exp_max?: number;
}

/**
 * Translate selected experience level(s) to the year range the API filters on.
 *
 * Accepts a single value, an array, or a CSV string. Multiple levels span
 * min(min)..max(max); a level with no upper bound (lead) drops exp_max.
 * Unknown values are ignored so a stale UI option can never break the search.
 */
export function experienceLevelsToYears(
  value: string | string[] | undefined | null
): ExperienceYears {
  if (!value) return {};
  const items = Array.isArray(value) ? value : String(value).split(",");
  let min: number | undefined;
  let max: number | undefined;
  let unbounded = false;
  for (const item of items) {
    const range = parseExperienceRange(String(item));
    if (!range) continue;
    min = min === undefined ? range.min : Math.min(min, range.min);
    if (range.max === null) unbounded = true;
    else max = max === undefined ? range.max : Math.max(max, range.max);
  }
  const out: ExperienceYears = {};
  if (min !== undefined) out.exp_min = min;
  if (max !== undefined && !unbounded) out.exp_max = max;
  return out;
}
